// Logs a single "App start" event on every page load, and keeps a small
// boot journal in localStorage so starts that happened while the device
// was offline are still visible retroactively: every event embeds the
// previous few boot records (`prevBoots`), so even if an event never
// reached the backend, the next successful start reports it.
//
// This exists to diagnose boot-time whitescreens: "no App start logged"
// becomes distinguishable between "the bundle never executed" and "it
// executed but couldn't deliver the event".
import { Logger } from "@touchtech/web-logging";

import { LocalStorageKeys } from "@is/constants";
import { LocalStorageService } from "@is/services";

import {
  drainAppSessionHeartbeat,
  startAppSessionHeartbeat,
} from "./appSessionHeartbeat";
import {
  drainAppSessionLiveness,
  startAppSessionLiveness,
} from "./appSessionLiveness";
import { markBootStage } from "./bootCrumbs";

type BootRecord = {
  at: string;
  navigationType?: string;
  online?: boolean;
  sessionId?: string;
  appVersion?: string;
  // Set once the app has rendered successfully (see markAppStartCompleted).
  // A boot that never gets this is a boot-time whitescreen.
  completed?: boolean;
};

// Journal is capped so localStorage never grows unbounded; the payload
// embeds fewer still to keep events small.
const JOURNAL_LIMIT = 10;
const PREV_BOOTS_IN_PAYLOAD = 5;

// Timestamp of the current boot's journal record, so markAppStartCompleted
// can find and flag exactly this boot once render succeeds.
let currentBootAt: string | undefined;

function readItem(key: LocalStorageKeys): string | undefined {
  try {
    const value = LocalStorageService.getItem(key);
    return typeof value === "string" && value.length > 0 ? value : undefined;
  } catch {
    return undefined;
  }
}

function readNavigationType(): string | undefined {
  try {
    const entries = performance.getEntriesByType("navigation");
    const navEntry = entries?.[0] as PerformanceNavigationTiming | undefined;
    return navEntry?.type;
  } catch {
    return undefined;
  }
}

function readOnline(): boolean | undefined {
  try {
    return typeof navigator !== "undefined" ? navigator.onLine : undefined;
  } catch {
    return undefined;
  }
}

// Synchronously available service-worker context: which SW (if any) is
// controlling this page right now. `swController` of null means no SW is in
// control yet — a fresh/uncontrolled load. This is the cheap signal we can
// attach to every "App start" without waiting on an async registration lookup.
function readServiceWorkerSync(): {
  swControlled: boolean;
  swControllerUrl?: string;
  swControllerState?: string;
} {
  try {
    const swContainer =
      typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
    const controller = swContainer?.controller;
    return {
      swControlled: Boolean(controller),
      swControllerUrl: controller?.scriptURL,
      swControllerState: controller?.state,
    };
  } catch {
    return { swControlled: false };
  }
}

// Asynchronously inspect the SW registration and emit a follow-up event. A
// `waiting` (or `installing`) worker means a new build has been downloaded but
// the old one is still in control — the "stuck update" precursor we suspect is
// behind the boot-time whitescreens (the kiosk never closes every tab, so the
// waiting worker never activates). Fire-and-forget; never blocks startup.
function logServiceWorkerDetail(bootAt: string, sessionId?: string): void {
  try {
    const swContainer =
      typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
    if (!swContainer?.getRegistration) return;

    swContainer
      .getRegistration()
      .then((registration) => {
        try {
          Logger.instance.info("App start (service worker)", {
            "tenant-id": readItem(LocalStorageKeys.TenantId),
            "device-id": readItem(LocalStorageKeys.DeviceId),
            "store-id": readItem(LocalStorageKeys.StoreId),
            "session-id": sessionId,
            bootAt,
            controllerUrl: swContainer.controller?.scriptURL,
            controllerState: swContainer.controller?.state,
            hasRegistration: Boolean(registration),
            // The stuck-update signals: a worker downloaded/activated but not
            // yet controlling the page.
            hasWaiting: Boolean(registration?.waiting),
            hasInstalling: Boolean(registration?.installing),
            waitingUrl: registration?.waiting?.scriptURL,
            installingUrl: registration?.installing?.scriptURL,
            activeUrl: registration?.active?.scriptURL,
            activeState: registration?.active?.state,
          });
        } catch {
          // delivery is best-effort
        }
      })
      .catch(() => {
        // registration lookup is best-effort
      });
  } catch {
    // never break startup over telemetry
  }
}

function readJournal(): BootRecord[] {
  try {
    const raw = LocalStorageService.getItem(LocalStorageKeys.BootJournal);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeJournal(journal: BootRecord[]): void {
  try {
    LocalStorageService.setItem(
      LocalStorageKeys.BootJournal,
      JSON.stringify(journal.slice(-JOURNAL_LIMIT))
    );
  } catch {
    // storage full / unavailable — journal is best-effort
  }
}

// White-screen reports are written by the inline watchdog/heartbeat in
// index.html (outside the bundle) straight to localStorage. We drain them here
// on the next successful boot and embed them in the App start payload, then
// clear — same "stash now, deliver on next boot" model as the boot journal.
const WHITE_SCREEN_KEY = "whiteScreenReports";

function drainWhiteScreenReports(): unknown[] {
  try {
    const raw = window.localStorage.getItem(WHITE_SCREEN_KEY);
    if (!raw) return [];
    window.localStorage.removeItem(WHITE_SCREEN_KEY);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// ---- healthy-boot baseline -------------------------------------------------
// The white-screen sensors only fire on FAILED boots, which leaves no control
// group: nothing says what a normal cold boot's timings look like on this
// fleet. The helpers below attach the browser's own measurements of THIS
// (successful) boot to every "App start" — same assets, same hours, zero
// extra I/O — so failing records can be compared against the healthy
// distribution.

// Matches the app's entry bundle under both naming schemes: webpack's
// bundle.<hash>.js (the old fleet still runs it) and Vite's
// assets/index-<hash>.js module entry.
const BUNDLE_NAME_RE = /(?:bundle\.[0-9a-f]+|assets\/index-[\w-]+)\.js/;

// The build's git commit, stamped into index.html's <head> at build time by the
// indexBuildStampPlugin in vite.config.mts. Ground truth for WHICH build's
// index.html (and inline white-screen watchdog) this boot loaded — the shell
// analogue of `runningBundle`. The shell is served/cached separately from the
// app chunk, so a device on a stale service worker keeps loading an old shell;
// this is the field that reveals that directly. Compare against the latest
// deployed commit to spot devices stuck on an old shell.
function readBuildCommit(): string | undefined {
  try {
    const w = window as unknown as { __buildCommit?: string };
    return typeof w.__buildCommit === "string" ? w.__buildCommit : undefined;
  } catch {
    return undefined;
  }
}

// The bundle URL the served index actually references. Unlike the
// socket-announced `appVersion` (which is bumped ahead of what really runs),
// this is ground truth for which build THIS boot executed.
function readRunningBundle(): string | undefined {
  try {
    const s =
      document.querySelector('script[src*="bundle"]') ||
      document.querySelector('script[type="module"][src]');
    const src = s ? (s as HTMLScriptElement).src : undefined;
    return src ? src.slice(-60) : undefined;
  } catch {
    return undefined;
  }
}

// How the bundle actually loaded on this boot (already measured by the
// browser): duration + whether it was served by the SW/cache (transferSize 0).
function readBundleTiming(): { ms: number; fromCache: boolean } | undefined {
  try {
    const entries = performance.getEntriesByType(
      "resource"
    ) as PerformanceResourceTiming[];
    for (const r of entries) {
      if (BUNDLE_NAME_RE.test(r.name)) {
        return { ms: Math.round(r.duration), fromCache: r.transferSize === 0 };
      }
    }
  } catch {
    // best-effort
  }
  return undefined;
}

// How long the service worker took to wake and serve the navigation, and
// whether index.html came from the SW cache or the network.
function readNavServeTiming():
  | { swWakeMs?: number; indexFromCache?: boolean }
  | undefined {
  try {
    const n = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (!n) return undefined;
    return {
      swWakeMs:
        n.workerStart > 0
          ? Math.round(n.responseStart - n.workerStart)
          : undefined,
      indexFromCache: n.transferSize === 0,
    };
  } catch {
    return undefined;
  }
}

// SW update lifecycle events observed by the inline script (index.html) this
// boot. Updates happen on HEALTHY boots, so this is where install failures
// ("…state:redundant") become visible fleet-wide, not just on white screens.
function readSwUpdateEvents(): Array<{ e?: string; t?: number }> {
  try {
    const w = window as unknown as { __swUpd?: Array<{ e?: string }> };
    return Array.isArray(w.__swUpd) ? w.__swUpd.slice() : [];
  } catch {
    return [];
  }
}

export function logAppStart(): void {
  try {
    const record: BootRecord = {
      at: new Date().toISOString(),
      navigationType: readNavigationType(),
      online: readOnline(),
      sessionId: readItem(LocalStorageKeys.SessionId),
      appVersion: readItem(LocalStorageKeys.Version),
    };

    const journal = readJournal();
    const previousBoot = journal[journal.length - 1];

    currentBootAt = record.at;

    const sw = readServiceWorkerSync();
    const whiteScreens = drainWhiteScreenReports();
    // How the PREVIOUS app session died: heartbeat drained from localStorage.
    // Short `aliveMs` = a fast renderer crash/hang (class 2); long `aliveMs`
    // with a climbing heap = a leak. The class-2 white screens are invisible to
    // the DOM watchdog, so this run-up is our only in-app signal for them.
    const prevAppSession = drainAppSessionHeartbeat();
    // Liveness events the watchdog stashed during the previous session but
    // couldn't deliver live (e.g. a freeze that needed a manual Edge kill).
    // Self-recovering blips are usually delivered live; this is the backstop.
    const prevLiveness = drainAppSessionLiveness();

    try {
      Logger.instance.info("App start", {
        "tenant-id": readItem(LocalStorageKeys.TenantId),
        "device-id": readItem(LocalStorageKeys.DeviceId),
        "store-id": readItem(LocalStorageKeys.StoreId),
        "session-id": record.sessionId,
        appVersion: record.appVersion,
        online: record.online,
        // Which SW build (if any) controls this page at startup — surfaces
        // version skew between the controlling worker and the bundle.
        swControlled: sw.swControlled,
        swControllerUrl: sw.swControllerUrl,
        swControllerState: sw.swControllerState,
        // Did the previous boot ever finish rendering? `false` here is a
        // boot-time whitescreen surfaced directly (without unpacking prevBoots).
        prevBootCompleted: previousBoot
          ? previousBoot.completed === true
          : undefined,
        prevBoots: journal.slice(-PREV_BOOTS_IN_PAYLOAD),
        // --- healthy-boot baseline: what a NORMAL boot's delivery looked
        // like, for comparison against the white-screen sensor readings.
        // Which shell (index.html/watchdog) loaded, alongside which bundle ran:
        // together they reveal shell-vs-bundle staleness on every boot.
        buildCommit: readBuildCommit(),
        runningBundle: readRunningBundle(),
        bundleLoad: readBundleTiming(),
        navServe: readNavServeTiming(),
        // White-screen reports captured by the inline watchdog/heartbeat on the
        // previous (failed) session, delivered now on the recovering boot.
        ...(whiteScreens.length ? { whiteScreens } : {}),
        // How the previous app session died (heartbeat run-up): the only in-app
        // signal for the class-2 renderer death/hang the DOM watchdog can't see.
        ...(prevAppSession ? { prevAppSession } : {}),
        // Mid-session liveness faults (blank/paint/stall) the watchdog couldn't
        // deliver live before the session ended.
        ...(prevLiveness ? { prevLiveness } : {}),
      });
    } catch {
      // delivery is best-effort; the journal below still records this boot
    }

    // Best-effort, non-blocking: emit the async SW registration detail.
    logServiceWorkerDetail(record.at, record.sessionId);

    // SW update checks run seconds AFTER boot, so a snapshot taken now would
    // miss them. Flush a one-off late snapshot, and only when something
    // actually happened (an update was found / installed / failed) — a
    // "…state:redundant" here is a FAILED install, observed on healthy boots
    // where updates actually occur.
    try {
      setTimeout(() => {
        try {
          const events = readSwUpdateEvents();
          const interesting = events.some(
            (x) =>
              typeof x?.e === "string" &&
              /updatefound|redundant|installing|waiting/.test(x.e)
          );
          if (!interesting) return;
          Logger.instance.info("SW update activity", {
            "tenant-id": readItem(LocalStorageKeys.TenantId),
            "device-id": readItem(LocalStorageKeys.DeviceId),
            "store-id": readItem(LocalStorageKeys.StoreId),
            buildCommit: readBuildCommit(),
            runningBundle: readRunningBundle(),
            events,
          });
        } catch {
          // best-effort
        }
      }, 20000);
    } catch {
      // best-effort
    }

    writeJournal([...journal, record]);
  } catch {
    // telemetry must never break app startup
  }
}

// Call once the app has rendered successfully (React committed the tree).
// Flags the current boot as completed so the NEXT boot can report whether this
// one finished — an uncompleted boot in prevBoots is a boot-time whitescreen.
export function markAppStartCompleted(): void {
  try {
    // Tell the inline watchdog the app actually painted. Lets it avoid false
    // positives on slow-but-healthy boots, and mark a late-recovered boot
    // (painted after the 8s threshold) so we can tell it from a permanent blank.
    try {
      (window as unknown as { __appBooted?: () => void }).__appBooted?.();
    } catch {
      // best-effort
    }
    // final boot-crumb: every stage before this one completed.
    markBootStage("app-rendered");

    // The app rendered successfully — begin the app-session heartbeat so that
    // if the renderer dies later (class 2), the NEXT boot can drain how long
    // this app session actually lived. Started here (not at boot) so `aliveMs`
    // measures time since a healthy render.
    startAppSessionHeartbeat();

    // Watch this app session for mid-session faults the boot watchdog can't see
    // (blank `#root`, a stalled compositor, a blocked main thread) - the blue
    // blank / white freeze class that leaves no telemetry today. Reports live,
    // and stashes for next-boot delivery if the session dies before it can.
    startAppSessionLiveness();

    if (!currentBootAt) return;

    const journal = readJournal();
    const index = journal.findIndex((boot) => boot.at === currentBootAt);
    if (index === -1) return;

    journal[index] = { ...journal[index], completed: true };
    writeJournal(journal);
  } catch {
    // best-effort; never break the app
  }
}
